import React, { useState } from 'react';
import { MapPinIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { SearchWithAutocomplete, Suggestion } from '../../../../components/UI/SearchWithAutocomplete';
import { SETTING_TYPES, ATMOSPHERE_TAGS, TIME_PERIODS } from '../genreModuleData';
const ACCENT = 'bg-teal-100 text-teal-700 border-teal-500 dark:bg-teal-900/30 dark:text-teal-300';
// Build suggestion lists
const settingSuggestions: Suggestion[] = SETTING_TYPES.map(s => ({
  id: s.toLowerCase().replace(/\s+/g, '-'),
  label: s,
  type: 'Setting'
}));
const atmosphereSuggestions: Suggestion[] = ATMOSPHERE_TAGS.map(a => ({
  id: a.toLowerCase().replace(/\s+/g, '-'),
  label: a,
  type: 'Atmosphere'
}));
const timePeriodSuggestions: Suggestion[] = TIME_PERIODS.map(p => ({
  id: p.toLowerCase().replace(/\s+/g, '-'),
  label: p,
  type: 'Period'
}));
export const WorldSettingSection: React.FC = () => {
  const dk = useDarkClasses();
  // ─── Setting Type ───
  const [selectedSettings, setSelectedSettings] = useState<string[]>([]);
  const [settingSearch, setSettingSearch] = useState('');
  // ─── Atmosphere / Scene Tags ───
  const [selectedAtmosphere, setSelectedAtmosphere] = useState<string[]>([]);
  const [atmosphereSearch, setAtmosphereSearch] = useState('');
  // ─── Time Period ───
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
  const [timePeriodSearch, setTimePeriodSearch] = useState('');
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-21440" data-mp="3ht4f89sqbo7v">
      <h2 className={`text-lg font-bold mb-2 flex items-center ${dk.textHeading}`} data-id="element-21441" data-mp="3hskvcnjiqpr7">
        <MapPinIcon className="w-5 h-5 mr-2 text-teal-500" data-id="element-21442" data-mp="3vrubxobmzlvp" />
        World & Setting
      </h2>
      <p className={`text-xs mb-6 ${dk.textMuted}`} data-id="element-21443" data-mp="3quc11se26wl2">
        Describe where and when your story takes place. Atmosphere tags help
        readers find books with specific vibes and scenes.
      </p>

      {/* ─── Setting Type ─── */}
      <div className="mb-8" data-id="element-21444" data-mp="cmvkix4vj0m">
        <label className={`block text-sm font-bold mb-1 ${dk.textPrimary}`} data-id="element-21445" data-mp="deti0e7dsu2">
          Setting Type
        </label>
        <SearchWithAutocomplete value={settingSearch} onChange={setSettingSearch} suggestions={settingSuggestions.filter(s => !selectedSettings.includes(s.label))} placeholder="e.g. Small Town, Urban, Medieval, Space Station..." onSelect={item => {
        setSelectedSettings(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
        setSettingSearch('');
      }} data-id="element-21446" data-mp="5c9cp3g31jep" />
        {selectedSettings.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-21447" data-mp="x2e1deavkslq">
            {selectedSettings.map(s => <span key={s} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${ACCENT}`} data-id="element-21448" data-mp="121yxzi2koova">
                {s}
                <button onClick={() => setSelectedSettings(prev => prev.filter(x => x !== s))} className="ml-1.5 hover:opacity-75" data-id="element-21449" data-mp="1h4gvcrxtxx8g">
                  <XIcon className="w-3 h-3" data-id="element-21450" data-mp="177m48u3vyz47" />
                </button>
              </span>)}
          </div>}
      </div>

      {/* ─── Atmosphere / Scene Tags ─── */}
      <div className="mb-8" data-id="element-21451" data-mp="1zvhvvkelm0u4">
        <label className={`block text-sm font-bold mb-1 ${dk.textPrimary}`} data-id="element-21452" data-mp="1zuqcr926tvkq">
          Atmosphere & Scene Tags
        </label>
        <p className={`text-xs mb-2 ${dk.textMuted}`} data-id="element-21453" data-mp="1uyhpt9hr0y30">
          Think about the vibes and specific scenes in your book — dark forests,
          isolated cabins, neon-lit streets, cozy bookshops.
        </p>
        <SearchWithAutocomplete value={atmosphereSearch} onChange={setAtmosphereSearch} suggestions={atmosphereSuggestions.filter(a => !selectedAtmosphere.includes(a.label))} placeholder="e.g. Dark Forest, Cozy Bookshop, Neon-Lit Streets..." onSelect={item => {
        setSelectedAtmosphere(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
        setAtmosphereSearch('');
      }} data-id="element-21454" data-mp="2ilpupqb9m54i" />
        {selectedAtmosphere.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-21455" data-mp="1tfzl01kxq1g9">
            {selectedAtmosphere.map(tag => <span key={tag} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${ACCENT}`} data-id="element-21456" data-mp="2h5ke3dasyf39">
                {tag}
                <button onClick={() => setSelectedAtmosphere(prev => prev.filter(x => x !== tag))} className="ml-1.5 hover:opacity-75" data-id="element-21457" data-mp="289cui234swfb">
                  <XIcon className="w-3 h-3" data-id="element-21458" data-mp="34ud46t4qtspy" />
                </button>
              </span>)}
          </div>}
      </div>

      {/* ─── Time Period ─── */}
      <div data-id="element-21459" data-mp="odyqedgu05m2">
        <label className={`block text-sm font-bold mb-1 ${dk.textPrimary}`} data-id="element-21460" data-mp="oegt9sy01i2y">
          Time Period
        </label>
        {selectedTimePeriod ? <div className="flex items-center gap-2 mt-1" data-id="element-21461" data-mp="y532n5f4hcd7">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${ACCENT}`} data-id="element-21462" data-mp="y5bdkrroq7lr">
              {selectedTimePeriod}
              <button onClick={() => setSelectedTimePeriod('')} className="ml-1.5 hover:opacity-75" data-id="element-21463" data-mp="5h77w6945uj2">
                <XIcon className="w-3 h-3" data-id="element-21464" data-mp="kb22c78ivhaq" />
              </button>
            </span>
          </div> : <SearchWithAutocomplete value={timePeriodSearch} onChange={setTimePeriodSearch} suggestions={timePeriodSuggestions} placeholder="e.g. Victorian, Modern Day, Medieval, Futuristic..." onSelect={item => {
        setSelectedTimePeriod(item.label);
        setTimePeriodSearch('');
      }} data-id="element-21465" data-mp="1vsbiokwctt7t" />}
      </div>
    </div>;
};